"use client";

import {
	pipelinePages,
	pipelineStages,
} from "@marketing/shared/lib/bento-fixtures";
import { Badge } from "@ui/components/badge";
import { cn } from "@ui/lib";
import { CHART_SERIES } from "@ui/lib/chart-palette";
import { CheckIcon, XIcon } from "lucide-react";
import { useRevealed } from "./motion";

/**
 * The flagship marketing animation: pages flowing through AI's retrieval
 * pipeline, surviving a stage or falling out of it. Pure CSS: each row's bar
 * grows to its exit stage with a staggered transition-delay once the nearest
 * Reveal fires; markers fade in after their bar arrives. Survivor bars wear
 * the brand chart slot; drop-outs go muted and always carry an icon + reason,
 * so state never rides on color alone.
 */
export function PipelineStagesBento({ className }: { className?: string }) {
	const revealed = useRevealed();
	const stageCount = pipelineStages.length;
	const survivorsAt = (stage: number) =>
		pipelinePages.filter((p) => p.exitStage > stage).length;

	// One template for the header and every row, so bar lengths line up with
	// the stage column boundaries and "Cited" gets its own outcome column.
	const gridTemplate = `minmax(120px, 1.3fr) repeat(${stageCount}, 1fr) 60px`;

	return (
		<div className={cn("space-y-4", className)}>
			<div className="grid gap-x-3" style={{ gridTemplateColumns: gridTemplate }}>
				{/* Stage header row */}
				<span />
				{pipelineStages.map((stage, i) => (
					<div key={stage.key} className="min-w-0">
						<p className="truncate text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
							{stage.label}
						</p>
						<p className="mt-0.5 font-mono text-xs tabular-nums text-muted-foreground">
							{survivorsAt(i - 1)} in · {survivorsAt(i)} out
						</p>
					</div>
				))}
				<span />
			</div>

			<ul className="space-y-2.5">
				{pipelinePages.map((page, rowIndex) => {
					const survived = page.exitStage >= stageCount;
					const widthPct = Math.min(
						100,
						(page.exitStage / stageCount) * 100,
					);
					return (
						<li
							key={page.id}
							className="grid items-center gap-x-3"
							style={{ gridTemplateColumns: gridTemplate }}
						>
							<span className="truncate font-mono text-xs text-muted-foreground">
								{page.page}
							</span>
							<span
								className="relative flex h-6 items-center"
								style={{
									gridColumn: `2 / span ${stageCount}`,
								}}
							>
								<span className="absolute inset-x-0 h-1.5 rounded-full bg-muted" />
								<span
									className="absolute h-1.5 rounded-full motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out"
									style={{
										width: revealed ? `${widthPct}%` : "0%",
										backgroundColor: survived
											? CHART_SERIES[0]
											: "var(--muted-foreground)",
										opacity: survived ? 1 : 0.45,
										transitionDelay: `${rowIndex * 140}ms`,
									}}
								/>
								{!survived ? (
									<span
										className={cn(
											"absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center motion-safe:transition-opacity motion-safe:duration-300",
											revealed
												? "opacity-100"
												: "opacity-0",
										)}
										style={{
											// Nudge at 0% so the first stage's
											// X stays inside the tile.
											left: `max(${widthPct}%, 10px)`,
											transitionDelay: `${rowIndex * 140 + 650}ms`,
										}}
									>
										<span className="flex size-5 items-center justify-center rounded-full border bg-background text-muted-foreground">
											<XIcon
												className="size-3"
												aria-hidden
											/>
										</span>
									</span>
								) : null}
							</span>
							<span
								className={cn(
									"motion-safe:transition-opacity motion-safe:duration-300",
									revealed ? "opacity-100" : "opacity-0",
								)}
								style={{
									transitionDelay: `${rowIndex * 140 + 650}ms`,
								}}
							>
								{survived ? (
									<Badge
										status="success"
										className="flex w-fit items-center gap-1 whitespace-nowrap"
									>
										<CheckIcon
											className="size-3"
											aria-hidden
										/>
										Cited
									</Badge>
								) : (
									<Badge
										status="error"
										className="flex w-fit items-center"
										aria-label="Not selected for the answer"
									>
										<XIcon
											className="size-3"
											aria-hidden
										/>
									</Badge>
								)}
							</span>
						</li>
					);
				})}
			</ul>

			<div className="space-y-1 border-t pt-3">
				{pipelinePages
					.filter((p) => p.exitReason)
					.slice(0, 2)
					.map((p) => (
						<p
							key={p.id}
							className="truncate text-xs text-muted-foreground"
						>
							<span className="font-mono">{p.page}</span> ·{" "}
							{p.exitReason}
						</p>
					))}
			</div>
		</div>
	);
}
